import { memo, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useChatbotMediator } from "../../../hooks/use-chatbot-manager/use-chatbot-mediator";
import type { MarkdownMessageParserProps } from "./ai-response-markdown-message-parser.interface";
import AiResponseHeaderText from "./components/header-text/ai-response-markdown-header-text";
import AiResponseMarkdownLink from "./components/link/ai-response-markdown-link";
import AiResponseParagraph from "./components/paragraph/ai-response-markdown-header-text";

const baseIntervalInMilliseconds = 5;
const maximumIntervalInMilliseconds = 50;
const intervalScalingFactor = 0.01;

function AiResponseMarkdownMessageParser({ markdownString, animate = false }: Readonly<MarkdownMessageParserProps>) {
  const [displayedText, setDisplayedText] = useState<string>("");
  const chatbotMediator = useChatbotMediator();

  useEffect(() => {
    if (!animate) {
      return;
    }

    chatbotMediator.setStartStreamingResponse();

    const printIntervalInMilliseconds = Math.min(
      Math.max(
        baseIntervalInMilliseconds / (markdownString.length * intervalScalingFactor),
        baseIntervalInMilliseconds
      ),
      maximumIntervalInMilliseconds
    );

    setDisplayedText("");
    const chars = markdownString.split("");
    let currentCharacterIndex = 0;

    const intervalId = setInterval(() => {
      setDisplayedText((prevText) => prevText + chars[currentCharacterIndex]);
      currentCharacterIndex += 1;

      const areAllCharsRendered = currentCharacterIndex === chars.length;
      if (areAllCharsRendered) {
        clearInterval(intervalId);
        chatbotMediator.setEndStreamingResponse();
      }
    }, printIntervalInMilliseconds);

    return function clearIntervalOnComponentUnmount() {
      clearInterval(intervalId);
    };
  }, [animate, chatbotMediator, markdownString]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: AiResponseMarkdownLink,
        h1: ({ children }) => <AiResponseHeaderText variant="h1">{children}</AiResponseHeaderText>,
        h2: ({ children }) => <AiResponseHeaderText variant="h2">{children}</AiResponseHeaderText>,
        h3: ({ children }) => <AiResponseHeaderText variant="h3">{children}</AiResponseHeaderText>,
        h4: ({ children }) => <AiResponseHeaderText variant="h4">{children}</AiResponseHeaderText>,
        h5: ({ children }) => <AiResponseHeaderText variant="h5">{children}</AiResponseHeaderText>,
        h6: ({ children }) => <AiResponseHeaderText variant="h6">{children}</AiResponseHeaderText>,
        p: AiResponseParagraph,
      }}
    >
      {animate ? displayedText : markdownString}
    </ReactMarkdown>
  );
}

export default memo(AiResponseMarkdownMessageParser, function compareMarkdownMessages(prevProps, nextProps) {
  return prevProps.markdownString === nextProps.markdownString;
});
